import { useState, useEffect, Suspense, lazy } from "react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LandingPage } from "@/components/LandingPage";
import { Toaster } from "@/components/ui/toaster";
import { Loader2 } from "lucide-react";

// Lazy load heavy components
const AuthForm = lazy(() => import("@/components/AuthForm").then(module => ({ default: module.AuthForm })));
const Dashboard = lazy(() => import("@/components/Dashboard").then(module => ({ default: module.Dashboard })));
const DemoDashboard = lazy(() => import("@/components/DemoDashboard").then(module => ({ default: module.DemoDashboard })));
const AdminDashboard = lazy(() => import("@/components/AdminDashboard").then(module => ({ default: module.AdminDashboard })));
const PrivacyPolicy = lazy(() => import("@/components/PrivacyPolicy").then(module => ({ default: module.PrivacyPolicy })));
const OnboardingView = lazy(() => import("@/components/OnboardingView").then(module => ({ default: module.OnboardingView })));
const CookieConsent = lazy(() => import("@/components/CookieConsent").then(module => ({ default: module.CookieConsent })));
const BlogList = lazy(() => import("@/components/BlogList").then(module => ({ default: module.BlogList })));
const BlogPost = lazy(() => import("@/components/BlogPost").then(module => ({ default: module.BlogPost })));
const SupportPage = lazy(() => import("@/components/SupportPage").then(module => ({ default: module.SupportPage })));
import { OnboardingModal } from "@/components/OnboardingModal";
import { TermsModal } from "@/components/TermsModal";
import { LandingService } from "@/lib/db";

// Loading fallback
const PageLoader = () => (
    <div className="flex h-screen w-full items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
);

function AppContent() {
    const { user, profile, loading } = useAuth();
    const [view, setView] = useState<'landing' | 'auth' | 'demo' | 'admin' | 'dashboard' | 'privacy' | 'onboarding' | 'blog-list' | 'blog-post' | 'support'>('landing');
    const [currentBlogId, setCurrentBlogId] = useState<string | null>(null);
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [showTerms, setShowTerms] = useState(false);
    const [latestTermsVersion, setLatestTermsVersion] = useState<string | null>(null);

    // Handle initial deep linking
    useEffect(() => {
        const path = window.location.pathname;
        if (path === '/privacy' || window.location.hash === '#privacy') {
            setView('privacy');
        } else if (path === '/support') {
            setView('support');
        } else if (path === '/blogs') {
            setView('blog-list');
        } else if (path.startsWith('/blogs/')) {
            const id = path.split('/')[2];
            if (id) {
                setCurrentBlogId(id);
                setView('blog-post');
            }
        }

        // Fetch latest terms version
        const fetchLatestTerms = async () => {
            try {
                const content = await LandingService.getLandingContent();
                if (content?.termsVersion) {
                    setLatestTermsVersion(content.termsVersion);
                }
            } catch (error) {
                console.error("Failed to fetch latest terms version:", error);
            }
        };
        fetchLatestTerms();
    }, []);

    // Handle back button (popstate)
    useEffect(() => {
        const handlePopState = () => {
            const path = window.location.pathname;
            if (path === '/') {
                if (user) setView('dashboard');
                else setView('landing');
            }
            else if (path === '/support') setView('support');
            else if (path === '/blogs') setView('blog-list');
            else if (path.startsWith('/blogs/')) {
                const id = path.split('/')[2];
                setCurrentBlogId(id);
                setView('blog-post');
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    // ... (auth effect remains same)
    useEffect(() => {
        if (loading) return;

        if (user) {
            if (view === 'auth' || view === 'landing') {
                setView('dashboard');
                window.history.pushState({}, "", "/");
            }
        } else {
            // Allow public views
            if (!['demo', 'auth', 'privacy', 'blog-list', 'blog-post', 'onboarding', 'support'].includes(view)) {
                setView('landing');
            }
        }

        // Modal triggers
        if (user) {
            const signedVersion = (profile as any)?.acceptedTermsVersion;
            const hasAccepted = profile?.termsAccepted || false;

            // A user is compliant if they have accepted terms AND (we don't know the latest version yet OR their version matches the latest)
            const isCompliant = hasAccepted &&
                (!latestTermsVersion || !signedVersion || latestTermsVersion === signedVersion);

            console.log("[App] Terms Check:", {
                hasProfile: !!profile,
                termsAccepted: hasAccepted,
                signedVersion,
                latestTermsVersion,
                isCompliant,
                view
            });

            if (!isCompliant) {
                setShowTerms(true);
            } else {
                setShowTerms(false);
            }
        }
    }, [user, profile, loading, view, latestTermsVersion]);

    if (loading) {
        return <div className="flex h-screen items-center justify-center">Loading...</div>;
    }

    const content = (
        <>
            {view === 'landing' && (
                <LandingPage
                    onLogin={() => setView('auth')}
                    onDemo={() => setView('demo')}
                    onGetStarted={() => setView('onboarding')}
                    onPrivacy={() => {
                        setView('privacy');
                        window.history.pushState({}, "", "/privacy");
                    }}
                    onViewAllBlogs={() => {
                        setView('blog-list');
                        window.history.pushState({}, "", "/blogs");
                    }}
                    onReadBlog={(id) => {
                        setCurrentBlogId(id);
                        setView('blog-post');
                        window.history.pushState({}, "", `/blogs/${id}`);
                    }}
                    onSupport={() => {
                        setView('support');
                        window.history.pushState({}, "", "/support");
                    }}
                />
            )}

            <Suspense fallback={<PageLoader />}>
                {view === 'auth' && <AuthForm onBack={() => setView('landing')} />}
                {view === 'demo' && <DemoDashboard onBack={() => setView('landing')} />}

                {user && view === 'dashboard' && (
                    <Dashboard
                        onAdminClick={() => {
                            setView('admin');
                            window.history.pushState({}, "", "/admin");
                        }}
                        onSupportClick={() => {
                            setView('support');
                            window.history.pushState({}, "", "/support");
                        }}
                    />
                )}

                {user && view === 'admin' && profile?.role === 'admin' && (
                    <AdminDashboard onBack={() => setView('dashboard')} />
                )}

                {/* Public Views being accessed while logged in or out */}
                {view === 'blog-list' && (
                    <BlogList
                        onBack={() => {
                            if (user) setView('dashboard');
                            else setView('landing');
                            window.history.pushState({}, "", "/");
                        }}
                        onRead={(id) => {
                            setCurrentBlogId(id);
                            setView('blog-post');
                            window.history.pushState({}, "", `/blogs/${id}`);
                        }}
                    />
                )}

                {view === 'blog-post' && currentBlogId && (
                    <BlogPost
                        blogId={currentBlogId}
                        onBack={() => {
                            setView('blog-list');
                            window.history.pushState({}, "", "/blogs");
                        }}
                    />
                )}

                {view === 'privacy' && (
                    <PrivacyPolicy onBack={() => {
                        if (user) setView('dashboard');
                        else setView('landing');
                        window.history.pushState({}, "", "/");
                    }} />
                )}

                {view === 'support' && (
                    <SupportPage onBack={() => {
                        if (user) setView('dashboard');
                        else setView('landing');
                        window.history.pushState({}, "", "/");
                    }} />
                )}

                {view === 'onboarding' && (
                    <OnboardingView
                        onContinue={() => setView('auth')}
                        onBack={() => setView('landing')}
                    />
                )}

                <CookieConsent onPrivacyClick={() => {
                    setView('privacy');
                    window.history.pushState({}, "", "/privacy");
                }} />

                {/* Onboarding & Terms - Only show when in App (not on public pages) */}
                {!['landing', 'auth', 'demo', 'privacy', 'blog-list', 'blog-post', 'support'].includes(view) && (
                    <>
                        <OnboardingModal open={showOnboarding} onComplete={() => setShowOnboarding(false)} />
                        <TermsModal open={showTerms} onAccept={() => {
                            setShowTerms(false);
                            // skip onboarding modal
                        }} />
                    </>
                )}
            </Suspense>
        </>
    );

    return content;
}

export default function App() {
    return (
        <AuthProvider>
            <AppContent />
            <Toaster />
        </AuthProvider>
    );
}
